import { Suspense, memo } from 'react';
import { Routes, Route, Navigate, type Location } from 'react-router-dom';

import { getAllArtifacts } from '@/artifacts';

interface MapBackgroundRoutesProps {
  /**
   * Location used to match background routes. When an overlay is active,
   * callers pass the stored background location instead of the current
   * location so the panel stays mounted under the overlay.
   */
  readonly location: Location;
}

/**
 * Background routes for the `/map` modal pattern.
 *
 * Rendered by `MapView` using the "background location" (which may be the
 * current location or `location.state.backgroundLocation` when an overlay
 * is active). This keeps the tasks/notes page mounted under an overlay
 * instead of unmounting on overlay navigation.
 *
 * Route segments here are relative to the parent `map/*` route in the
 * application router. The route tree is derived from `getAllArtifacts()`:
 *
 * 1. Plural background routes (`/map/<id>`) — normal list/kanban entry
 *    points, one per registered artifact.
 * 2. Overlay fallback routes (`/map/<singular>/*`) — handle cold-load /
 *    refresh on an overlay URL so the background stays rendered under
 *    the overlay (defaults to the artifact that matches the singular
 *    segment, not a hardcoded one).
 *
 * Adding a new artifact requires no changes here — registering it in
 * `src/artifacts/registry.ts` is sufficient.
 */
export const MapBackgroundRoutes = memo(function MapBackgroundRoutes({
  location,
}: MapBackgroundRoutesProps) {
  const artifacts = getAllArtifacts();
  const defaultArtifactId = artifacts[0]?.id ?? 'tasks';

  return (
    <Routes location={location}>
      <Route index element={<Navigate to={defaultArtifactId} replace />} />

      {/* Plural background routes (`/map/<id>`). */}
      {artifacts.map((artifact) => {
        const BackgroundPage = artifact.BackgroundPage;
        return (
          <Route
            key={`background-${artifact.id}`}
            path={artifact.id}
            element={
              <Suspense fallback={null}>
                <BackgroundPage />
              </Suspense>
            }
          />
        );
      })}

      {/*
        Overlay fallback routes. When an overlay URL like `/map/task/:id`
        is matched directly (cold-load / refresh), the matching artifact's
        background page stays mounted underneath the overlay.
      */}
      {artifacts.map((artifact) => {
        const BackgroundPage = artifact.BackgroundPage;
        return (
          <Route
            key={`overlay-${artifact.singular}`}
            path={`${artifact.singular}/*`}
            element={
              <Suspense fallback={null}>
                <BackgroundPage />
              </Suspense>
            }
          />
        );
      })}

      {/* Unknown sub-path under /map → default to the first artifact. */}
      <Route path="*" element={<Navigate to={defaultArtifactId} replace />} />
    </Routes>
  );
});
